import { useEffect, useMemo, useRef, useState } from "react";
import { IngredientGlyph } from "../../components/IngredientGlyph";
import { LocaleSwitcher } from "../../components/LocaleSwitcher";
import { ingredientById } from "../../data/ingredients";
import { recipeById } from "../../data/recipes";
import {
  candidateForRecipe,
  selectionFromState,
} from "../../domain/candidates";
import {
  formatAmount,
  instantiateRecipe,
} from "../../domain/instantiateRecipe";
import {
  localizedIngredientName,
  localizedPreparation,
} from "../../domain/localize";
import { pluralize } from "../../domain/pluralize";
import {
  COOKING_LOCATIONS,
  FOOD_TYPES,
  type AppState,
  type Locale,
} from "../../domain/types";

interface Props {
  state: AppState;
  locale?: Locale;
  onLocaleChange?: (locale: Locale) => void;
  onBack: () => void;
  onSwap: (slotId: string, ingredientId: string) => void;
  onShare: () => void;
}

const GROUP_LABELS = {
  selected: "You selected",
  add: "Add these",
  pantry: "Pantry staples",
  optional: "Optional finishing touches",
} as const;

function joinFitReasons(reasons: string[]): string {
  if (reasons.length < 2) return reasons[0] ?? "";
  if (reasons.length === 2) return reasons.join(" and ");
  return `${reasons.slice(0, -1).join(", ")}, and ${reasons.at(-1)}`;
}

function stepCompletionKey(recipeId: string, stepId: string, text: string) {
  return JSON.stringify([recipeId, stepId, text]);
}

export function RecipeDetail({
  state,
  locale = "en",
  onLocaleChange = () => {},
  onBack,
  onSwap,
  onShare,
}: Props) {
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const titleRef = useRef<HTMLHeadingElement>(null);
  const selection = useMemo(() => selectionFromState(state), [state]);
  const family = useMemo(
    () => (state.recipeId ? recipeById.get(state.recipeId) : undefined),
    [state.recipeId],
  );
  const candidate = useMemo(
    () =>
      state.recipeId ? candidateForRecipe(state.recipeId, selection) : null,
    [state.recipeId, selection],
  );
  const recipe = useMemo(
    () =>
      family && candidate
        ? instantiateRecipe(
            family,
            candidate,
            state.ingredientIds,
            state.swaps,
            state.veganOnly,
            locale,
          )
        : null,
    [
      family,
      candidate,
      state.ingredientIds,
      state.swaps,
      state.veganOnly,
      locale,
    ],
  );
  const slotById = useMemo(
    () => new Map(family?.slots.map((s) => [s.id, s]) ?? []),
    [family],
  );
  const foodTypeLabel = FOOD_TYPES.find(
    (option) => option.id === family?.foodType,
  )?.label;
  const cookingLocationLabel = COOKING_LOCATIONS.find(
    (option) => option.id === family?.primaryCookingLocation,
  )?.label;
  const fitReasons = [
    state.ingredientIds.length > 0
      ? (candidate?.matchedIngredientCount ?? 0) === state.ingredientIds.length
        ? `uses all ${state.ingredientIds.length} selected ${pluralize(state.ingredientIds.length, locale, { en: ["ingredient", "ingredients"] })}`
        : `uses ${candidate?.matchedIngredientCount ?? 0} of ${state.ingredientIds.length} selected ingredients`
      : null,
    state.foodTypeIds.length > 0 && foodTypeLabel
      ? `matches your ${foodTypeLabel.toLowerCase()} choice`
      : null,
    state.cookingLocationIds.length > 0 && cookingLocationLabel
      ? `uses your ${cookingLocationLabel.toLowerCase()} setup`
      : null,
    state.veganOnly ? "uses only vegan options" : null,
  ].filter((reason): reason is string => reason !== null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  if (!family || !candidate || !recipe) return null;

  const completedStepCount = recipe.steps.filter((step) =>
    completedSteps.has(stepCompletionKey(family.id, step.id, step.text)),
  ).length;

  function toggleStep(key: string) {
    setCompletedSteps((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <main className="recipe-page">
      <header className="recipe-page__header">
        <button className="back-button" onClick={onBack}>
          <span aria-hidden="true">←</span> Back to builder
        </button>
        <LocaleSwitcher locale={locale} onChange={onLocaleChange} />
        <button className="text-button" onClick={onShare}>
          Share recipe
        </button>
      </header>
      <article className="recipe-sheet">
        <header className="recipe-intro">
          <div>
            <p className="eyebrow">Your recipe ticket</p>
            <p className="recipe-context">
              <span>{foodTypeLabel}</span>
              <span>{cookingLocationLabel}</span>
            </p>
            <h1 ref={titleRef} tabIndex={-1}>
              {recipe.title}
            </h1>
            <p>
              Built from your current choices, with substitutions limited to
              this recipe.
            </p>
          </div>
          <div className="recipe-facts">
            <div>
              <strong>{family.activeMinutes}</strong>
              <span>active min</span>
            </div>
            <div>
              <strong>{family.totalMinutes}</strong>
              <span>total min</span>
            </div>
            <div>
              <strong>{family.servings}</strong>
              <span>servings</span>
            </div>
            <div>
              <strong>{recipe.vegan ? "V" : "VG"}</strong>
              <span>{recipe.vegan ? "vegan" : "vegetarian"}</span>
            </div>
          </div>
        </header>

        <section className="fit-note" aria-label="Why this fits">
          <span className="fit-note__mark" aria-hidden="true">
            ✓
          </span>
          <div>
            <strong>Why this fits</strong>
            <p>
              {fitReasons.length > 0
                ? `It ${joinFitReasons(fitReasons)}.`
                : "It is a flexible starting recipe."}
            </p>
          </div>
        </section>

        <div className="recipe-content">
          <div className="ingredient-groups">
            {(["selected", "add", "pantry", "optional"] as const).map(
              (group) => {
                const items = recipe.ingredients.filter(
                  (item) => item.group === group,
                );
                if (items.length === 0) return null;
                return (
                  <section
                    className={`ingredient-group ingredient-group--${group}`}
                    key={group}
                  >
                    <h2>{GROUP_LABELS[group]}</h2>
                    <ul>
                      {items.map((item) => {
                        const slot = slotById.get(item.slotId);
                        const swapOptions =
                          slot?.options.filter(
                            (option) =>
                              !state.veganOnly ||
                              ingredientById
                                .get(option.ingredientId)
                                ?.dietTags.includes("vegan"),
                          ) ?? [];
                        const preparation = localizedPreparation(
                          item.option,
                          locale,
                        );
                        return (
                          <li key={item.slotId}>
                            <div>
                              <span className="ingredient-name">
                                <IngredientGlyph
                                  ingredientId={item.ingredient.id}
                                  category={item.ingredient.category}
                                />
                                <strong>
                                  {localizedIngredientName(
                                    item.ingredient,
                                    locale,
                                  )}
                                </strong>
                              </span>
                              <span className="ingredient-amount">
                                {formatAmount(item.option)}
                                {preparation ? ` · ${preparation}` : ""}
                              </span>
                            </div>
                            {!item.locked && swapOptions.length > 1 && (
                              <label className="swap-control">
                                <span>Swap {item.slotLabel}</span>
                                <select
                                  value={item.ingredient.id}
                                  onChange={(event) =>
                                    onSwap(item.slotId, event.target.value)
                                  }
                                >
                                  {swapOptions.map((option) => {
                                    const swapIngredient = ingredientById.get(
                                      option.ingredientId,
                                    );
                                    return (
                                      <option
                                        key={option.ingredientId}
                                        value={option.ingredientId}
                                      >
                                        {swapIngredient
                                          ? localizedIngredientName(
                                              swapIngredient,
                                              locale,
                                            )
                                          : option.ingredientId}
                                      </option>
                                    );
                                  })}
                                </select>
                              </label>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              },
            )}
            <aside className="allergen-note">
              <strong>Allergens in this version</strong>
              <p>
                {recipe.allergens.length
                  ? recipe.allergens.join(", ")
                  : "None identified in the ingredient data."}
              </p>
              <small>
                Check every product label and cross-contamination warning
                yourself.
              </small>
            </aside>
          </div>

          <section className="method-section">
            <div className="section-heading">
              <div>
                <span className="section-kicker">Method</span>
                <h2>Cook it</h2>
              </div>
              <span>
                {completedStepCount}/{recipe.steps.length} done
              </span>
            </div>
            <ol className="steps-list">
              {recipe.steps.map((step, index) => {
                const completionKey = stepCompletionKey(
                  family.id,
                  step.id,
                  step.text,
                );
                const completed = completedSteps.has(completionKey);
                return (
                  <li className={completed ? "is-complete" : ""} key={step.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={completed}
                        onChange={() => toggleStep(completionKey)}
                      />
                      <span className="step-number" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span>{step.text}</span>
                    </label>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>
      </article>
    </main>
  );
}
